import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from './Dashboard';

const API = 'https://tribaltech-backend.onrender.com';

export default function Leaks() {
  const [leaks, setLeaks]       = useState([]);
  const [stats, setStats]       = useState({ total_leaks: 0, dmca_filed: 0, removed: 0, pending: 0 });
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [filing, setFiling]     = useState(null);
  const [queueing, setQueueing] = useState(null);
  const [checking, setChecking] = useState(null);
  const [tineyeing, setTineyeing] = useState(null);
  const [reverseMatches, setReverseMatches] = useState({}); // leak.id -> { tineye: count }

  const token   = localStorage.getItem('tribal_token');
  const headers = { Authorization: 'Bearer ' + token };

  useEffect(() => {
    axios.get(API + '/api/leaks', { headers })
      .then(r => {
        // 2026-10-05: /api/leaks now returns { leaks, stats }. Tolerate the
        // pre-2026-10-05 array shape too so an older cached client still works.
        if (Array.isArray(r.data)) {
          setLeaks(r.data);
        } else {
          setLeaks(r.data?.leaks || []);
          if (r.data?.stats) setStats(r.data.stats);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const fileDMCA = async (url) => {
    setFiling(url);
    try {
      await axios.post(API + '/api/leaks/manual-dmca', { url }, { headers });
      // Backend auto-queues deindex when DMCA fires, so reflect that locally too.
      setLeaks(leaks.map(l => l.leaked_url === url
        ? { ...l, dmca_filed: true, deindex_status: l.deindex_status || 'queued' }
        : l));
    } catch (e) {
      alert('DMCA filing failed: ' + (e.response?.data?.error || e.message));
    }
    setFiling(null);
  };

  const queueDeindex = async (leak) => {
    setQueueing(leak.id);
    try {
      await axios.post(API + '/api/leaks/' + leak.id + '/deindex', {}, { headers });
      setLeaks(leaks.map(l => l.id === leak.id ? { ...l, deindex_status: 'queued' } : l));
    } catch (e) {
      alert('Deindex queue failed: ' + (e.response?.data?.error || e.message));
    }
    setQueueing(null);
  };

  const openYandex = async (leak) => {
    try {
      const r = await axios.post(API + '/api/leaks/' + leak.id + '/reverse-search/yandex', {}, { headers });
      if (r.data?.search_url) window.open(r.data.search_url, '_blank', 'noopener');
    } catch (e) {
      alert('Yandex search unavailable: ' + (e.response?.data?.error || e.message));
    }
  };

  const runTineye = async (leak) => {
    setTineyeing(leak.id);
    try {
      const r = await axios.post(API + '/api/leaks/' + leak.id + '/reverse-search/tineye', {}, { headers });
      setReverseMatches(prev => ({ ...prev, [leak.id]: { tineye: r.data.matches_found || 0 } }));
    } catch (e) {
      const msg = e.response?.data?.error || e.message;
      if (e.response?.status === 503) alert('TinEye not configured yet (needs TINEYE_PUBLIC_KEY + TINEYE_PRIVATE_KEY)');
      else alert('TinEye failed: ' + msg);
    }
    setTineyeing(null);
  };

  const checkIndexed = async (leak) => {
    setChecking(leak.id);
    try {
      const r = await axios.post(API + '/api/leaks/' + leak.id + '/check-indexed', {}, { headers });
      setLeaks(leaks.map(l => l.id === leak.id
        ? { ...l, google_indexed: r.data.indexed, google_indexed_checked_at: new Date().toISOString() }
        : l));
    } catch (e) {
      alert('Indexed check failed: ' + (e.response?.data?.error || e.message));
    }
    setChecking(null);
  };

  const filtered = leaks.filter(l => {
    if (filter === 'pending') return !l.dmca_filed;
    if (filter === 'filed')   return l.dmca_filed;
    return true;
  });

  const platform = (url) => {
    try { return new URL(url).hostname.replace('www.',''); }
    catch { return 'Unknown'; }
  };

  return (
    <div className="page">
      <Sidebar />
      <div className="main">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 24 }}>
          <div className="page-title" style={{ marginBottom: 0 }}>Leaked content</div>
          <div style={{ display:'flex', gap: 8 }}>
            {['all','pending','filed'].map(f => (
              <button
                key={f}
                className={'btn ' + (filter === f ? 'btn-primary' : 'btn-secondary')}
                style={{ padding:'6px 14px', fontSize: 13 }}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 16, display:'flex', gap: 16 }}>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-label">Total leaks</div>
            <div className="stat-value" style={{ color:'var(--coral)', fontSize: 22 }}>{stats.total_leaks}</div>
          </div>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-label">DMCA filed</div>
            <div className="stat-value" style={{ color:'var(--green)', fontSize: 22 }}>{stats.dmca_filed}</div>
          </div>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-label">Removed</div>
            <div className="stat-value" style={{ color:'var(--blue)', fontSize: 22 }}>{stats.removed}</div>
          </div>
          <div className="stat-card" style={{ flex: 1 }}>
            <div className="stat-label">Pending</div>
            <div className="stat-value" style={{ color:'var(--gold)', fontSize: 22 }}>{stats.pending}</div>
          </div>
        </div>

        <div className="card">
          {loading ? (
            <div style={{ color:'var(--muted)', padding: 20 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ color:'var(--muted)', padding: '30px 0', textAlign:'center' }}>
              No leaks found matching this filter.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Platform</th>
                  <th>Cohort</th>
                  <th>Detected</th>
                  <th>Google</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((leak, i) => (
                  <tr key={leak.id || i}>
                    <td>
                      {leak._redacted ? (
                        // Identity-exposure host — server stripped leaked_url;
                        // render host as plain text, no link, no path/query.
                        <span style={{ fontSize: 12, color:'var(--muted)' }}
                              title="Operator-handled — URL hidden on identity-exposure host">
                          {leak.platform}
                        </span>
                      ) : (
                        <a href={leak.leaked_url} target="_blank" rel="noreferrer"
                          style={{ fontSize: 12, color:'var(--purple-light)' }}>
                          {leak.leaked_url?.substring(0,55)}...
                        </a>
                      )}
                      {leak.deepfake_suspected && (
                        <span
                          title={`Heuristic deepfake signals flagged (score ${leak.deepfake_score ?? '?'}). Face matched but artefacts suggest manipulation — review before filing.`}
                          style={{
                            marginLeft: 8, display: 'inline-block',
                            padding: '2px 7px', borderRadius: 4,
                            background: 'rgba(248,113,113,0.15)', color: '#f87171',
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
                            verticalAlign: 'middle',
                          }}
                        >
                          ⚠ DEEPFAKE?
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>{platform(leak.leaked_url)}</td>
                    <td style={{ fontSize: 12, color:'var(--muted)' }}>
                      {leak.cohort_id ? 'Cohort ' + String(leak.cohort_id).padStart(3,'0') : '—'}
                    </td>
                    <td style={{ fontSize: 12, color:'var(--muted)' }}>
                      {leak.detected_at ? new Date(leak.detected_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {leak.google_indexed === true
                        ? <span className="badge badge-red">Indexed</span>
                        : leak.google_indexed === false
                          ? <span className="badge badge-green">Not indexed</span>
                          : <button
                              className="btn btn-secondary"
                              style={{ padding:'2px 8px', fontSize: 11 }}
                              onClick={() => checkIndexed(leak)}
                              disabled={checking === leak.id}
                            >
                              {checking === leak.id ? 'Checking…' : 'Check'}
                            </button>}
                    </td>
                    <td>
                      <div style={{ display:'flex', flexDirection:'column', gap: 2 }}>
                        {leak.dmca_filed
                          ? <span className="badge badge-green">DMCA filed</span>
                          : <span className="badge badge-red">DMCA pending</span>}
                        {leak.deindex_status === 'queued' && <span className="badge badge-gold">Deindex queued</span>}
                        {leak.deindex_status === 'exported' && <span className="badge badge-green">Deindex submitted</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ display:'flex', gap: 6 }}>
                        {/*
                         * 2026-10-04: sweep-sourced rows carry a server-
                         * computed _display { label, hide_file_button }.
                         * When present, the row is operator-managed —
                         * File DMCA button is suppressed and a status
                         * label is shown instead. Non-sweep rows keep
                         * the pre-existing render path (button + badges).
                         * Server also enforces this via a 409 on
                         * POST /api/leaks/:id/dmca; the UI condition is
                         * UX, not security.
                         */}
                        {leak._display ? (
                          <span
                            className={
                              leak._display.label === 'removed'                ? 'badge badge-green' :
                              leak._display.label === 'no_longer_online'      ? 'badge badge-grey'  :
                              leak._display.label === 'filed'                  ? 'badge badge-blue'  :
                              leak._display.label === 'different_route_handled' ? 'badge badge-grey' :
                                                                                  'badge badge-gold'
                            }
                            title={
                              leak._display.label === 'no_longer_online'
                                ? 'URL went offline independently of our DMCA. Not counted as a removal win.'
                                : 'Operator-managed; our team files these notices directly.'
                            }
                          >
                            {leak._display.label === 'removed'                ? 'Removed' :
                             leak._display.label === 'no_longer_online'      ? 'No longer online' :
                             leak._display.label === 'filed'                  ? 'Filed' :
                             leak._display.label === 'different_route_handled' ? 'Being handled directly by our team' :
                                                                                 'Found, filing in progress'}
                          </span>
                        ) : (
                          <>
                            {!leak.dmca_filed && !leak.is_safe && !leak.needs_review && leak.has_route === false && (
                              <span className="badge badge-grey"
                                    title="No DMCA filing route is registered for this host yet. Our team is adding it.">
                                Filing route being set up
                              </span>
                            )}
                            {!leak.dmca_filed && !leak.is_safe && !leak.needs_review && leak.has_route !== false && (
                              <button
                                className="btn btn-danger"
                                style={{ padding:'4px 10px', fontSize: 12 }}
                                onClick={() => fileDMCA(leak.leaked_url)}
                                disabled={filing === leak.leaked_url}
                              >
                                {filing === leak.leaked_url ? 'Filing…' : 'File DMCA'}
                              </button>
                            )}
                            {leak.needs_review && !leak.dmca_filed && (
                              <span className="badge badge-gold" title="Awaiting operator review">Pending review</span>
                            )}
                            {leak.is_safe && !leak.dmca_filed && (
                              <span className="badge badge-grey" title="Marked false positive">Not actionable</span>
                            )}
                          </>
                        )}
                        {!leak.deindex_status && (
                          <button
                            className="btn btn-secondary"
                            style={{ padding:'4px 10px', fontSize: 12 }}
                            onClick={() => queueDeindex(leak)}
                            disabled={queueing === leak.id}
                          >
                            {queueing === leak.id ? 'Queueing…' : 'Queue deindex'}
                          </button>
                        )}
                        <button
                          className="btn btn-secondary"
                          style={{ padding:'4px 10px', fontSize: 12 }}
                          onClick={() => runTineye(leak)}
                          disabled={tineyeing === leak.id}
                          title="Search TinEye for this image"
                        >
                          {tineyeing === leak.id
                            ? 'TinEye…'
                            : reverseMatches[leak.id]?.tineye != null
                              ? `TinEye (${reverseMatches[leak.id].tineye})`
                              : 'TinEye'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          style={{ padding:'4px 10px', fontSize: 12 }}
                          onClick={() => openYandex(leak)}
                          title="Open this image in Yandex reverse search"
                        >
                          Yandex ↗
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
